/**
 * Single source of truth for check-in status display and chip colours.
 * Used by Check-Ins page and Dashboard so chips stay consistent everywhere.
 */

export type CheckInStatusChipColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning';

const LABELS: Record<string, string> = {
  Scheduled: 'Scheduled',
  AwaitingManagerDecision: 'Awaiting Manager Decision',
  AwaitingEmployeeAcknowledgment: 'Awaiting Employee',
  LatePendingAcknowledgment: 'Late - Pending',
  MissedPendingAcknowledgment: 'Missed - Pending',
  Completed: 'Completed',
  Cancelled: 'Cancelled (frequency removed)',
};

/** Human-readable label for a check-in status. */
export function formatCheckInStatus(status: string): string {
  if (LABELS[status]) return LABELS[status];
  return status
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/** MUI Chip color for check-in status. Each status has a distinct colour. */
export function getCheckInStatusColor(status: string): CheckInStatusChipColor {
  switch (status) {
    case 'Scheduled':
      return 'default';
    case 'AwaitingManagerDecision':
      return 'info';
    case 'AwaitingEmployeeAcknowledgment':
      return 'primary';
    case 'LatePendingAcknowledgment':
      return 'warning';
    case 'MissedPendingAcknowledgment':
    case 'Missed':
      return 'error';
    case 'Completed':
      return 'success';
    case 'Cancelled':
      return 'default';
    case 'Pending':
      return 'secondary';
    default:
      return 'default';
  }
}

/** All known check-in status values (for replacing raw status in audit text). */
export const CHECK_IN_STATUS_VALUES = [
  'Scheduled',
  'AwaitingManagerDecision',
  'AwaitingEmployeeAcknowledgment',
  'LatePendingAcknowledgment',
  'MissedPendingAcknowledgment',
  'Completed',
  'Cancelled',
] as const;

/** Replace raw status strings in text with formatted labels (e.g. audit trail details). */
export function formatCheckInStatusInText(text: string): string {
  let result = text;
  CHECK_IN_STATUS_VALUES.forEach((status) => {
    result = result.replace(new RegExp(status, 'g'), formatCheckInStatus(status));
  });
  return result;
}
